import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getMyPermissions } from '../api/client';

const PermissionContext = createContext({
  permissions: [],
  loading: true,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  refreshPermissions: async () => {},
  clearPermissions: () => {},
});

export function PermissionProvider({ children }) {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissions([]);
      setLoading(false);
      return;
    }
    try {
      const res = await getMyPermissions();
      setPermissions(res.data.permissions || []);
    } catch {
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount (handles page refresh with existing token)
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const refreshPermissions = useCallback(async () => {
    setLoading(true);
    await fetchPermissions();
  }, [fetchPermissions]);

  const clearPermissions = useCallback(() => {
    setPermissions([]);
  }, []);

  const hasPermission = useCallback(
    (code) => permissions.includes(code),
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (codes) => codes.some((c) => permissions.includes(c)),
    [permissions]
  );

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        loading,
        hasPermission,
        hasAnyPermission,
        refreshPermissions,
        clearPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionContext);
}

export default PermissionContext;
